import { useLayoutEffect, useRef } from "react";

import { buildTrackingTimeline, type TrackingTimeline, type TrackingTimelineInputs } from "./timeline-status";
import type { TrackingSelectionIndex } from "./selection";
import type { PreparedSpatialSegment } from "../../components/overlay/types";

interface PublicationInputs extends TrackingTimelineInputs {
  /** Changes for source, active strategy, or explicit rerun revisions. */
  resetKey: unknown;
  /** Stable while only tracking results change; defaults to the index's annotation map. */
  annotationRevision?: unknown;
  publishTimeline: (timeline: TrackingTimeline) => void;
  clearTimeline: (expected: TrackingTimeline) => void;
}

interface Publication extends PublicationInputs {
  timeline: TrackingTimeline;
  timer?: ReturnType<typeof setTimeout>;
}

const PUBLICATION_INTERVAL_MS = 100;

const samePoint = (a: { x: number; y: number }, b: { x: number; y: number }) => a.x === b.x && a.y === b.y;

function sameAnnotation(a: PreparedSpatialSegment, b: PreparedSpatialSegment): boolean {
  if (a === b) return true;
  if (
    a.type !== b.type ||
    a.trackId !== b.trackId ||
    a.interactionId !== b.interactionId ||
    a.startTime !== b.startTime ||
    a.endTime !== b.endTime
  )
    return false;
  // Appending a dense GT may shorten the preceding timestamp's displayUntil.
  // This is part of the permitted append batch, not an edit to that GT.
  if (a.coordinates === b.coordinates) return true;
  if (a.type === "point_box" && b.type === "point_box") {
    return (
      samePoint(a.coordinates.top_left, b.coordinates.top_left) &&
      samePoint(a.coordinates.bottom_right, b.coordinates.bottom_right)
    );
  }
  if (a.type === "point" && b.type === "point") return samePoint(a.coordinates, b.coordinates);
  if (a.type === "polygon" && b.type === "polygon") {
    return (
      a.coordinates.length === b.coordinates.length &&
      a.coordinates.every((point, i) => samePoint(point, b.coordinates[i]!))
    );
  }
  return false;
}

function classifyAnnotations(
  previous: TrackingSelectionIndex,
  next: TrackingSelectionIndex,
  published: TrackingTimeline,
): "reset" | "append" | "batch" {
  if (previous.byId.size > next.byId.size) return "reset";
  for (const [id, annotation] of previous.byId) {
    const replacement = next.byId.get(id);
    if (!replacement || !sameAnnotation(annotation, replacement)) return "reset";
  }
  // Rows fall back to their raw geometry when a cluster has no published key.
  // Reserve new box clusters immediately; existing-cluster appends can wait.
  for (const annotation of next.byId.values()) {
    if (
      annotation.type === "point_box" &&
      next.anchorTracks.has(annotation.id) &&
      !published.coverage.has(annotation.interactionId ?? annotation.id)
    )
      return "append";
  }
  return "batch";
}

function disposePublication(publication: Publication) {
  clearTimeout(publication.timer);
  // The store only clears this exact publication, preserving a newer publisher.
  publication.clearTimeline(publication.timeline);
}

/** Batch timeline projection work; video selection continues consuming every result immediately. */
export function useTrackingTimelinePublication({
  index,
  items,
  intervals,
  activity,
  errors,
  durationSeconds,
  resetKey,
  annotationRevision = index.byId,
  publishTimeline,
  clearTimeline,
}: PublicationInputs): void {
  const current = useRef<Publication | null>(null);

  useLayoutEffect(() => {
    const previous = current.current;
    const reset =
      !previous ||
      previous.resetKey !== resetKey ||
      !Object.is(previous.durationSeconds, durationSeconds) ||
      previous.publishTimeline !== publishTimeline ||
      previous.clearTimeline !== clearTimeline;
    const annotations =
      previous && previous.annotationRevision !== annotationRevision
        ? classifyAnnotations(previous.index, index, previous.timeline)
        : "batch";
    if (reset || annotations !== "batch") {
      if (previous) disposePublication(previous);
      // Replacing source/GT/settings must invalidate old bars before paint,
      // including when a prior result batch is still waiting on its timer.
      const timeline: TrackingTimeline = {
        ...buildTrackingTimeline({ index, items, intervals, activity, errors, durationSeconds }),
        revision: !reset && annotations === "append" ? previous!.timeline.revision : {},
      };
      current.current = {
        index,
        items,
        intervals,
        activity,
        errors,
        durationSeconds,
        resetKey,
        annotationRevision,
        publishTimeline,
        clearTimeline,
        timeline,
      };
      publishTimeline(timeline);
      return;
    }
    previous.annotationRevision = annotationRevision;
    if (
      previous.index === index &&
      previous.items === items &&
      previous.intervals === intervals &&
      previous.activity === activity &&
      previous.errors === errors
    )
      return;
    Object.assign(previous, { index, items, intervals, activity, errors });
    if (previous.timer !== undefined) return;
    // Keep the first update's deadline. Later results replace only the input,
    // so an uninterrupted stream still publishes within 100 ms.
    previous.timer = setTimeout(() => {
      if (current.current !== previous) return;
      previous.timer = undefined;
      previous.timeline = { ...buildTrackingTimeline(previous), revision: previous.timeline.revision };
      previous.publishTimeline(previous.timeline);
    }, PUBLICATION_INTERVAL_MS);
  }, [
    index,
    items,
    intervals,
    activity,
    errors,
    durationSeconds,
    resetKey,
    annotationRevision,
    publishTimeline,
    clearTimeline,
  ]);

  useLayoutEffect(
    () => () => {
      const publication = current.current;
      current.current = null;
      if (publication) disposePublication(publication);
    },
    [],
  );
}
